import { useMemo } from 'react';
import { useDraggable } from '@dnd-kit/core';
import type { Task } from '@cleartask/shared';
import { formatTime } from '../../lib/datetime.js';
import { useTimeZone } from '../../session.js';
import { useLists } from '../lists/useLists.js';
import { useCompleteTask, useReopenTask } from '../tasks/useTasks.js';

/** Cor de quem nao tem lista — a caixa de entrada do TickTick tambem e azul. */
const COR_SEM_LISTA = 'var(--brand-blue)';

/** Cor de cada lista, por id. Uma consulta so, em cache, para todos os blocos. */
function useCorDaLista(): (listId: string | null) => string {
  const { data } = useLists();

  return useMemo(() => {
    const cores = new Map((data?.lists ?? []).map((l) => [l.id, l.color]));
    return (listId) => (listId === null ? COR_SEM_LISTA : (cores.get(listId) ?? COR_SEM_LISTA));
  }, [data]);
}

interface TaskChipProps {
  task: Task;
  now: number;
  /** Mostra a hora a direita. Falso na faixa "dia inteiro". */
  showTime?: boolean;
  onOpen?: (task: Task) => void;
}

/**
 * Bloco de tarefa dentro do calendario, no desenho do TickTick (§8ab): barra
 * preenchida com a cor da LISTA, caixinha de concluir, titulo e hora a direita.
 *
 * A cor e da lista, e nao do quadrante como antes: no calendario a pergunta e
 * "de que frente e isto", e a urgencia ja esta dita pela posicao na grade.
 *
 * Concluidas continuam aparecendo, esmaecidas e com a caixinha marcada:
 * aconteceram naquele dia, e um calendario que as esconde mente sobre o que o
 * dia conteve.
 *
 * O BLOCO INTEIRO arrasta — nao ha mais alca "⠿". O que separa o toque de
 * abrir do gesto de arrastar sao os sensores da tela (`Calendar.tsx`): 8 px
 * de deslocamento com mouse, toque longo no celular. A caixinha e o titulo
 * continuam botoes de verdade.
 */
export function TaskChip({ task, showTime = true, onOpen }: TaskChipProps) {
  const timeZone = useTimeZone();
  const corDaLista = useCorDaLista();
  const completeTask = useCompleteTask();
  const reopenTask = useReopenTask();
  const { listeners, setNodeRef, transform, isDragging } = useDraggable({ id: task.id });

  const concluida = task.status === 'done';
  const cor = corDaLista(task.listId);
  const mostraHora = showTime && task.dueAt !== null && !task.allDay;

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      data-task-id={task.id}
      className={[
        'relative flex min-w-0 touch-manipulation items-center gap-1 overflow-hidden rounded-[3px] px-1 text-xs leading-[18px]',
        concluida ? 'opacity-50' : '',
        isDragging ? 'opacity-40' : '',
      ].join(' ')}
      style={{
        // Fundo claro tingido pela cor da lista, como o TickTick. `color-mix`
        // sobre a superficie acompanha o tema escuro sem uma segunda paleta.
        backgroundColor: `color-mix(in srgb, ${cor} 22%, var(--surface))`,
        ...(transform
          ? { transform: `translate3d(${transform.x}px, ${transform.y}px, 0)`, zIndex: 20 }
          : {}),
      }}
    >
      {/*
        Caixinha e hora so a partir do `md`. Na celula do mes no celular cabem
        uns 50 px: com as duas, sobravam tres letras do titulo — o "⠿ 0…" que
        motivou o redesenho. O TickTick mobile tambem mostra so o titulo ali.
      */}
      <input
        type="checkbox"
        checked={concluida}
        onChange={() => (concluida ? reopenTask.mutate(task) : completeTask.mutate(task))}
        // O pointerdown da caixinha nao pode virar inicio de arrasto.
        onPointerDown={(event) => event.stopPropagation()}
        aria-label={concluida ? `Reabrir ${task.title}` : `Concluir ${task.title}`}
        className="hidden h-3 w-3 shrink-0 cursor-pointer rounded-[2px] md:inline-block"
        style={{ accentColor: cor }}
      />

      <button
        type="button"
        onClick={() => onOpen?.(task)}
        className={[
          'min-w-0 flex-1 truncate text-left text-text',
          concluida ? 'line-through' : '',
        ].join(' ')}
      >
        {task.title}
      </button>

      {mostraHora ? (
        <span className="hidden shrink-0 text-[10px] tabular-nums text-text-mut md:inline">
          {formatTime(task.dueAt as number, timeZone)}
        </span>
      ) : null}
    </div>
  );
}
